import { useCallback, useMemo } from 'react';
import type { UserSession } from 'features/rosie/types';
import useUserStore from 'store/useUserStore';
import type {
  RosieAction,
  RosiePermission,
  RosieResource,
} from './permissions.types';
import { ROUTE_ACCESS_RULES } from './routeAccess.config';
import coarseResourceMatchesRulePath from './legacyPermissionAliases';
import { READ_ONLY_ROUTE_RULE_IDS } from './readOnlyRouteRuleIds';
import { ADMIN_ONLY_USER_TYPES } from './routeAccess.userTypes';

const COARSE_RESOURCES = new Set<RosieResource>([
  'txters',
  'my-txt',
  'scla',
  'hackathon',
  'seals',
  'seals-legacy',
  'labs',
  'automated-calls',
  'batch-sms',
  'equipment',
  'engagement-records',
  'office-check-in',
  'txt-merch',
  'txt-programs',
  'signed-forms',
  'activities',
  'surveys',
  'emails',
  'rosie-users',
  'rosie-roles',
  'bitwarden',
  'dropbox-audit',
  'bagel',
]);

function writeTwin(permission: RosiePermission): RosiePermission | null {
  if (permission === '*' || !permission.endsWith(':read')) {
    return null;
  }
  const i = permission.lastIndexOf(':read');
  return `${permission.slice(0, i)}:write` as RosiePermission;
}

function parseCoarsePermission(
  permission: string,
): { resource: RosieResource; action: 'read' | 'write' } | null {
  const sep = permission.lastIndexOf(':');
  if (sep <= 0) return null;
  const resource = permission.slice(0, sep) as RosieResource;
  const action = permission.slice(sep + 1);
  if (action !== 'read' && action !== 'write') return null;
  if (!COARSE_RESOURCES.has(resource)) return null;
  return { resource, action };
}

function permissionAction(
  permission: RosiePermission,
): RosieAction | null {
  if (permission === '*') return null;
  const sep = permission.lastIndexOf(':');
  if (sep <= 0) return null;
  return permission.slice(sep + 1) as RosieAction;
}

function ruleIdFromPermission(permission: RosiePermission): string | null {
  if (permission === '*') return null;
  const sep = permission.lastIndexOf(':');
  if (sep <= 0) return null;
  return permission.slice(0, sep);
}

function legacyCoarseAliasAllows(
  held: string,
  requested: RosiePermission,
): boolean {
  const coarse = parseCoarsePermission(held);
  if (!coarse) return false;

  const requestedAction = permissionAction(requested);
  const requestedRuleId = ruleIdFromPermission(requested);
  if (!requestedAction || !requestedRuleId) return false;

  const rule = ROUTE_ACCESS_RULES.find(r => r.id === requestedRuleId);
  if (!rule) return false;
  if (!coarseResourceMatchesRulePath(coarse.resource, rule.pathPrefix)) {
    return false;
  }

  if (requestedAction === 'read') {
    return coarse.action === 'read' || coarse.action === 'write';
  }
  if (requestedAction === 'write') {
    return coarse.action === 'write';
  }
  return false;
}

function blockedByAdminOnlyRule(
  userType: string | undefined,
  permission: RosiePermission,
): boolean {
  const ruleId = ruleIdFromPermission(permission);
  if (!ruleId) return false;
  const rule = ROUTE_ACCESS_RULES.find(r => r.id === ruleId);
  if (rule?.allowedUserTypes !== ADMIN_ONLY_USER_TYPES) return false;
  return !(ADMIN_ONLY_USER_TYPES as readonly string[]).includes(
    userType ?? '',
  );
}

/**
 * Pure permission resolver — no React dependency, safe to call in tests.
 *
 * Resolution order:
 * 0. Admin-only route rules (/admin, /emails) → deny unless user.type is admin
 * 1. user.permissions includes '*' → allow all
 * 2. user.permissions includes the specific permission → allow
 * 3. For `:read` checks, user has matching `...:write` (granular) → allow
 * 4. Legacy coarse `{resource}:read|write` covers granular rule permissions on that path
 * 5. Otherwise → deny
 *
 * Account `type` never grants access; it only restricts admin-only sections.
 */
export function resolvePermission(
  user: Pick<UserSession, 'type' | 'permissions'>,
  permission: RosiePermission,
): boolean {
  if (blockedByAdminOnlyRule(user.type, permission)) return false;
  if (user.permissions.includes('*')) return true;
  if (user.permissions.includes(permission)) return true;

  const twin = writeTwin(permission);
  if (twin && user.permissions.includes(twin)) return true;

  if (
    user.permissions.some(held =>
      legacyCoarseAliasAllows(held, permission),
    )
  ) {
    return true;
  }

  return false;
}

export function usePermissions(): {
  can: (permission: RosiePermission) => boolean;
} {
  const user = useUserStore(s => s.user);

  const can = useCallback(
    (permission: RosiePermission): boolean => {
      if (!user) return false;
      return resolvePermission(user, permission);
    },
    [user],
  );

  return { can };
}

/**
 * True if the user may write anywhere under the coarse resource namespace
 * (any granular `{ruleId}:write` whose route path matches the resource).
 */
export function useCanWrite(resource: RosieResource): boolean {
  const user = useUserStore(s => s.user);
  const { can } = usePermissions();

  return useMemo(() => {
    if (
      user?.permissions.includes('*') &&
      (ADMIN_ONLY_USER_TYPES as readonly string[]).includes(user.type)
    ) {
      return true;
    }
    return ROUTE_ACCESS_RULES.some(rule => {
      if (rule.id === 'staff' || READ_ONLY_ROUTE_RULE_IDS.has(rule.id)) {
        return false;
      }
      if (!coarseResourceMatchesRulePath(resource, rule.pathPrefix)) {
        return false;
      }
      return can(`${rule.id}:write` as RosiePermission);
    });
  }, [can, resource, user?.permissions, user?.type]);
}

/** True if the user may read the route identified by `ruleId`. */
export function useCanReadRule(ruleId: string): boolean {
  const { can } = usePermissions();
  return useMemo(
    () => can(`${ruleId}:read` as RosiePermission),
    [can, ruleId],
  );
}

/** True if the user may write on the route identified by `ruleId`. */
export function useCanWriteRule(ruleId: string): boolean {
  const { can } = usePermissions();
  return useMemo(
    () => can(`${ruleId}:write` as RosiePermission),
    [can, ruleId],
  );
}
